/** Fallback when the canvas scroller isn't found (e.g. tests). */
export const TOP_CLEARANCE = 48

/** Clears the left-panel icon rail when no panel is expanded. */
export const LEFT_CLEARANCE = 52

/** Clears the right-panel icon rail when no panel is expanded — same width as the left rail. */
export const RIGHT_CLEARANCE = 52

/**
 * Right edge of whatever the left panel currently occupies — just the icon rail, or the
 * icon rail plus an expanded properties panel (up to 320px). Read live from the DOM (same
 * `data-left-panel` selector used by photo-drop hit-testing) rather than assuming a fixed
 * width, since the panel can expand/collapse independent of any shape/scale/canvas change a
 * floating toolbar/slider otherwise reacts to. Shared by `NodeEditToolbar`, `CropToolbar`, and
 * `CropZoomSlider` — every floating chrome element anchored to a shape on canvas.
 */
export function getLeftBoundary(): number {
  const panelEl = document.querySelector<HTMLElement>('[data-left-panel="true"]')

  return panelEl ? panelEl.getBoundingClientRect().right : LEFT_CLEARANCE
}

/**
 * Left edge of whatever the right panel currently occupies — the expanded panel content (e.g.
 * the Photos tray) when open, or just its icon rail when closed. Mirrors `getLeftBoundary()` for
 * the opposite edge, reading the same `data-right-panel-shell`/`data-right-panel-rail` selectors
 * `isClientPointOverPhotoDropSurface` uses — the shell (when rendered) always sits to the LEFT of
 * the always-rendered rail, so it takes priority when present.
 */
export function getRightBoundary(): number {
  const shellEl = document.querySelector<HTMLElement>('[data-right-panel-shell="true"]')

  if (shellEl) return shellEl.getBoundingClientRect().left

  const railEl = document.querySelector<HTMLElement>('[data-right-panel-rail="true"]')

  if (railEl) return railEl.getBoundingClientRect().left

  return window.innerWidth - RIGHT_CLEARANCE
}

/**
 * The pannable canvas scroll container — top ruler and left ruler are pinned just inside its
 * edges, and any native horizontal scrollbar it renders (once a zoomed/panned document
 * overflows the viewport) sits along its bottom edge. Reading this live lets floating chrome
 * clear both the ruler and the app chrome above it (menu bar + editor toolbar), and the
 * scrollbar below it, without hardcoding their combined heights.
 */
export function getCanvasScrollerEl(): HTMLElement | null {
  return document.querySelector<HTMLElement>('[data-testid="center-canvas-scroller"]')
}

/**
 * Bottom edge of the app menu bar (the spread back/forward buttons). Popovers that grow taller than
 * the space below their anchor may shift up, but never above this line. 0 when the bar isn't
 * rendered (e.g. tests).
 */
export function getMenuBarBottom(): number {
  const el = document.querySelector<HTMLElement>('[data-app-menu-bar="true"]')

  return el ? el.getBoundingClientRect().bottom : 0
}

/** Top edge floating chrome must stay below — just under the horizontal ruler. */
export function getTopBoundary(): number {
  const el = getCanvasScrollerEl()

  return el ? el.getBoundingClientRect().top : TOP_CLEARANCE
}

/**
 * Bottom edge floating chrome must stay above — the scroller's real bottom minus the thickness
 * of any native horizontal scrollbar currently rendered (0 when the document fits or the
 * platform uses overlay scrollbars).
 */
export function getBottomBoundary(): number {
  const el = getCanvasScrollerEl()

  if (!el) return window.innerHeight

  const horizontalScrollbarThickness = el.offsetHeight - el.clientHeight

  return el.getBoundingClientRect().bottom - horizontalScrollbarThickness
}
